import { Component, HostListener, OnInit, output, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  CancelButtonComponent,
  CloseIconButtonComponent,
} from '@sandbox-platform/shared-ui';

import { ReverseCommissionPaymentDTO } from '../../../../types/commission-payment.types';

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CancelButtonComponent,
    CloseIconButtonComponent,
  ],
  selector: 'lib-reverse-payment-modal',
  template: `
    @if (isOpen()) {
    <div class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="fixed inset-0 bg-white/70 backdrop-blur-sm"
        (click)="close()"
        aria-hidden="true"
      ></div>

      <div
        class="relative flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
      >
        <span class="hidden sm:inline-block sm:h-screen sm:align-middle"
          >&#8203;</span
        >

        <div
          class="inline-block w-full transform rounded-lg border border-gray-200 bg-white pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-md sm:align-middle"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
          tabindex="0"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reverse-payment-title"
        >
          <div class="rounded-t-lg bg-slate-600 px-4 pb-3 pt-5 sm:px-6 sm:pt-4">
            <div class="flex items-center justify-between">
              <h3
                id="reverse-payment-title"
                class="text-lg font-medium text-slate-100"
              >
                Reverse Payment
              </h3>
              <lib-close-icon-button
                size="sm"
                variant="modal"
                colorScheme="dark"
                title="Close"
                ariaLabel="Close reverse payment modal"
                (clicked)="close()"
              />
            </div>
          </div>

          <form
            [formGroup]="form"
            (ngSubmit)="onSubmit()"
            class="flex flex-col gap-y-4 px-4 pt-4 sm:px-6"
          >
            <p class="text-sm text-gray-600">
              Reversing this payment will create an offsetting entry. Please
              provide a reason.
            </p>

            <div class="flex flex-col gap-y-1">
              <label
                for="reverse_reason"
                class="text-sm font-medium text-gray-700"
              >
                Reason <span class="text-red-500">*</span>
              </label>
              <textarea
                id="reverse_reason"
                formControlName="reason"
                rows="3"
                class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                placeholder="Explain why this payment is being reversed"
              ></textarea>
              @if (submitted() && form.get('reason')?.errors?.['required']) {
              <p class="text-xs text-red-600">Reason is required</p>
              }
            </div>

            <div class="flex justify-end gap-3 pb-2">
              <shared-cancel-button text="Cancel" (cancelled)="close()" />
              <button
                type="submit"
                class="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Reverse Payment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    }
  `,
})
export class ReversePaymentModalComponent implements OnInit {
  reverseSubmit = output<{
    paymentId: string;
    dto: ReverseCommissionPaymentDTO;
  }>();
  closed = output<void>();

  protected readonly isOpen = signal(false);
  protected readonly submitted = signal(false);
  protected form!: FormGroup;
  private paymentId = '';

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      reason: ['', Validators.required],
    });
  }

  open(paymentId: string): void {
    this.paymentId = paymentId;
    this.submitted.set(false);
    this.form.reset({ reason: '' });
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen()) {
      this.close();
    }
  }

  protected onSubmit(): void {
    this.submitted.set(true);
    if (this.form.invalid) return;

    const dto: ReverseCommissionPaymentDTO = {
      reason: this.form.value.reason,
    };

    this.reverseSubmit.emit({ paymentId: this.paymentId, dto });
    this.close();
  }
}
